// src/pages/LandingPage.jsx
import React, { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet";
import "./LandingPage.css";

const translations = {
  en: {
    title: "AI Survey Development Platform | survai",
    metaDescription:
      "Build, edit, and share intelligent, multilingual surveys in seconds with survai.",
    navbar: { subscribe: "Subscribe", login: "Login" },
    hero: {
      headline: "Generate high quality surveys in seconds",
      subheadline:
        "Create, edit, and share intelligent surveys—translated into eight languages.",
      cta: "Get Started for Free",
    },
    features: {
      benefit1: {
        title: "Knowledge-backed surveys in seconds",
        text:
          "Powered by industry expertise, survai’s AI engine is trained on years of professional survey development. Trust that every question is crafted using proven research methods.",
      },
      benefit2: {
        title: "Fine-tune, remix, and expand your questions effortlessly",
        text:
          "Simply tell survai what you need and our AI will instantly generate questions and answers. Edit and reorder them seamlessly.",
      },
      benefit3: {
        title: "Break language barriers with effortless translation",
        text:
          "With one click, survai transforms your survey into up to eight different languages—no extra setup required.",
      },
      benefit4: {
        title: "Gather feedback without forcing logins",
        text:
          "Quickly share a unique survey link with colleagues. Reviewers simply enter their name to participate and leave comments.",
        exampleButton: "See a live example",
      },
    },
    valueProposition: {
      speed: { title: "Speed & efficiency", text: "From idea to ready-to-send survey in minutes." },
      collaboration: { title: "Share & collaborate", text: "Invite unlimited collaborators effortlessly." },
      global: { title: "Translate instantly", text: "Expand to international audiences with instant translations." },
      noCoding: { title: "No coding required", text: "Perfect for marketers, researchers, and HR teams." },
      scalable: { title: "Scalable & secure", text: "Enterprise-grade security and flexible plans." },
    },
    faq: [
      {
        question: "How secure is my data?",
        answer:
          "Your data is protected with enterprise-grade security, encrypted storage, and regular audits.",
      },
      {
        question: "Can I customize my surveys?",
        answer:
          "Absolutely! Our intuitive editor lets you adjust every aspect of your survey.",
      },
      {
        question: "How does multilingual translation work?",
        answer:
          "With one click, survai leverages advanced AI to translate your survey into up to eight languages.",
      },
      {
        question: "What if I need help?",
        answer:
          "Our support team is available via chat and email 24/7 to assist with any questions.",
      },
      {
        question: "What subscription plans are available?",
        answer:
          "We offer flexible plans for individuals, teams, agencies, and enterprises, each tailored to your needs.",
      },
      {
        question: "How do I share my survey?",
        answer:
          "Generate a unique shareable link through our Share modal—respondents can review and comment without an account.",
      },
      {
        question: "Can I export my survey data?",
        answer:
          "Yes. You can export your survey and responses in PDF, CSV, and XLS formats.",
      },
      {
        question: "Do I need any coding skills?",
        answer:
          "Not at all—survai is designed for everyone, regardless of technical expertise.",
      },
      {
        question: "How fast can I generate a survey with survai?",
        answer:
          "Our AI-powered system takes you from idea to a fully functional survey in just minutes.",
      },
    ],
    finalCTA: {
      headline: "Ready to transform your surveys?",
      text: "Create, translate, and share your surveys effortlessly with survai.",
      button: "Get Started for Free",
    },
    footer: { copy: "© 2025 survai. All Rights Reserved." },
  },
  es: {
    title: "Plataforma de Desarrollo de Encuestas AI | survai",
    metaDescription:
      "Crea, edita y comparte encuestas inteligentes y multilingües en segundos con survai.",
    navbar: { subscribe: "Suscribirse", login: "Iniciar Sesión" },
    hero: {
      headline: "Genera encuestas de alta calidad en segundos",
      subheadline:
        "Crea, edita y comparte encuestas inteligentes—traducidas a ocho idiomas.",
      cta: "Comienza Gratis",
    },
    features: {
      benefit1: {
        title: "Encuestas basadas en conocimiento en segundos",
        text:
          "Con experiencia real en la industria, el motor de IA de survai se ha entrenado en años de desarrollo profesional de encuestas. Confía en que cada pregunta se formula con métodos de investigación comprobados.",
      },
      benefit2: {
        title: "Afina, remezcla y expande tus preguntas sin esfuerzo",
        text:
          "Solo di lo que necesitas y nuestra IA generará preguntas y respuestas al instante. Edita y reorganiza sin problemas.",
      },
      benefit3: {
        title: "Rompe barreras lingüísticas con traducción sin esfuerzo",
        text:
          "Con un clic, survai transforma tu encuesta a hasta ocho idiomas, sin necesidad de configuraciones adicionales.",
      },
      benefit4: {
        title: "Recoge feedback sin forzar inicios de sesión",
        text:
          "Comparte rápidamente un enlace único y permite que los usuarios participen dejando comentarios sin necesidad de registrarse.",
        exampleButton: "Ver ejemplo en vivo",
      },
    },
    valueProposition: {
      speed: { title: "Rapidez y Eficiencia", text: "De la idea a una encuesta lista en minutos." },
      collaboration: { title: "Colaboración", text: "Invita colaboradores ilimitados sin esfuerzo." },
      global: { title: "Alcance Global", text: "Expande a audiencias internacionales con traducciones instantáneas." },
      noCoding: { title: "Sin Necesidad de Programación", text: "Ideal para profesionales de marketing, investigadores y RRHH." },
      scalable: { title: "Escalable y Seguro", text: "Seguridad de nivel empresarial y planes flexibles." },
    },
    faq: [
      {
        question: "¿Qué tan seguros están mis datos?",
        answer:
          "Tus datos están protegidos con seguridad de nivel empresarial, almacenamiento cifrado y auditorías regulares.",
      },
      {
        question: "¿Puedo personalizar mis encuestas?",
        answer:
          "¡Por supuesto! Nuestro editor intuitivo te permite ajustar cada aspecto de tu encuesta.",
      },
      {
        question: "¿Cómo funciona la traducción multilingüe?",
        answer:
          "Con un solo clic, survai utiliza IA avanzada para traducir tu encuesta a hasta ocho idiomas.",
      },
      {
        question: "¿Qué hago si necesito ayuda?",
        answer:
          "Nuestro equipo de soporte está disponible 24/7 para asistirte a través de chat y correo electrónico.",
      },
      {
        question: "¿Qué planes de suscripción están disponibles?",
        answer:
          "Ofrecemos planes flexibles para individuos, equipos, agencias y empresas, cada uno con funciones adaptadas a tus necesidades.",
      },
      {
        question: "¿Cómo comparto mi encuesta?",
        answer:
          "Genera un enlace único a través de nuestro modal de compartición, y los participantes podrán ver y comentar sin necesidad de crear una cuenta.",
      },
      {
        question: "¿Puedo exportar los datos de mi encuesta?",
        answer:
          "Sí. Puedes exportar tus encuestas y respuestas en formatos PDF, CSV y XLS.",
      },
      {
        question: "¿Necesito habilidades de programación?",
        answer:
          "Para nada; survai está diseñado para todos, sin importar el conocimiento técnico.",
      },
      {
        question: "¿Qué tan rápido puedo generar una encuesta con survai?",
        answer:
          "Nuestro sistema basado en IA te lleva de una idea a una encuesta completamente funcional en cuestión de minutos.",
      },
    ],
    finalCTA: {
      headline: "¿Listo para transformar tus encuestas?",
      text: "Crea, traduce y comparte tus encuestas sin esfuerzo con survai.",
      button: "Comienza Gratis",
    },
    footer: { copy: "© 2025 survai. Todos los Derechos Reservados." },
  },
  fr: {
    title: "Plateforme de Développement d'Enquêtes AI | survai",
    metaDescription:
      "Créez, modifiez et partagez des enquêtes intelligentes et multilingues en quelques secondes avec survai.",
    navbar: { subscribe: "S'abonner", login: "Connexion" },
    hero: {
      headline: "Générez des enquêtes de haute qualité en quelques secondes",
      subheadline:
        "Créez, modifiez et partagez des enquêtes intelligentes—traduites en huit langues.",
      cta: "Commencez Gratuitement",
    },
    features: {
      benefit1: {
        title: "Enquêtes fondées sur la connaissance en quelques secondes",
        text:
          "Fort de l'expertise du secteur, le moteur d'IA de survai est entraîné sur des années de développement professionnel d'enquêtes. Chaque question est élaborée avec des méthodes de recherche éprouvées.",
      },
      benefit2: {
        title: "Affinez, remixez et étendez vos questions sans effort",
        text:
          "Indiquez simplement vos besoins à survai et notre IA générera instantanément des questions et réponses. Modifiez et réorganisez aisément.",
      },
      benefit3: {
        title: "Brisez les barrières linguistiques grâce à une traduction fluide",
        text:
          "En un clic, survai traduit votre enquête en jusqu'à huit langues, sans configuration supplémentaire.",
      },
      benefit4: {
        title: "Collectez des retours sans forcer la connexion",
        text:
          "Partagez rapidement un lien unique et laissez les utilisateurs donner leur avis sans créer de compte.",
        exampleButton: "Voir un exemple en direct",
      },
    },
    valueProposition: {
      speed: { title: "Vitesse & Efficacité", text: "De l’idée à une enquête prête en quelques minutes." },
      collaboration: { title: "Partage & Collaboration", text: "Invitez des collaborateurs illimités en toute simplicité." },
      global: { title: "Portée Globale", text: "Touchez un public international grâce à des traductions instantanées." },
      noCoding: { title: "Sans Codage", text: "Parfait pour les marketeurs, chercheurs et équipes RH." },
      scalable: { title: "Scalable & Sécurisé", text: "Sécurité de niveau entreprise et plans flexibles." },
    },
    faq: [
      {
        question: "Quelle est la sécurité de mes données ?",
        answer:
          "Vos données sont protégées par une sécurité de niveau entreprise, un stockage crypté et des audits réguliers.",
      },
      {
        question: "Puis-je personnaliser mes enquêtes ?",
        answer:
          "Oui, notre éditeur intuitif vous permet d’ajuster chaque aspect de votre enquête.",
      },
      {
        question: "Comment fonctionne la traduction multilingue ?",
        answer:
          "En un clic, survai utilise une IA avancée pour traduire votre enquête en huit langues.",
      },
      {
        question: "Que faire si j’ai besoin d’aide ?",
        answer:
          "Notre équipe d’assistance est disponible 24/7 par chat et e-mail pour vous aider.",
      },
      {
        question: "Quels sont les plans d’abonnement disponibles ?",
        answer:
          "Nous proposons des plans flexibles pour particuliers, équipes, agences et entreprises, avec des fonctionnalités adaptées à vos besoins.",
      },
      {
        question: "Comment partager mon enquête ?",
        answer:
          "Générez un lien unique via notre module de partage et distribuez-le – les utilisateurs peuvent consulter et commenter sans compte.",
      },
      {
        question: "Puis-je exporter mes données ?",
        answer:
          "Oui, vous pouvez exporter vos enquêtes et réponses en formats PDF, CSV ou XLS.",
      },
      {
        question: "Ai-je besoin de compétences en programmation ?",
        answer:
          "Pas du tout – survai est conçu pour tous, sans nécessiter de connaissances techniques.",
      },
      {
        question: "À quelle vitesse puis-je générer une enquête avec survai ?",
        answer:
          "Notre système d’IA transforme votre idée en une enquête fonctionnelle en quelques minutes.",
      },
    ],
    finalCTA: {
      headline: "Prêt à transformer vos enquêtes ?",
      text: "Créez, traduisez et partagez vos enquêtes sans effort avec survai.",
      button: "Commencez Gratuitement",
    },
    footer: { copy: "© 2025 survai. Tous droits réservés." },
  },
  de: {
    title: "KI-Umfrageentwicklungsplattform | survai",
    metaDescription:
      "Erstellen, bearbeiten und teilen Sie intelligente, mehrsprachige Umfragen in Sekunden mit survai.",
    navbar: { subscribe: "Abonnieren", login: "Anmelden" },
    hero: {
      headline: "Erstellen Sie hochwertige Umfragen in Sekunden",
      subheadline:
        "Erstellen, bearbeiten und teilen Sie intelligente Umfragen – übersetzt in acht Sprachen.",
      cta: "Kostenlos starten",
    },
    features: {
      benefit1: {
        title: "Wissenbasierte Umfragen in Sekunden",
        text:
          "Unterstützt durch Branchenexpertise, wurde survais KI-Engine über Jahre der professionellen Umfrageentwicklung trainiert. Jede Frage wird mit bewährten Forschungsmethoden erstellt.",
      },
      benefit2: {
        title: "Fragen mühelos anpassen, remixen und erweitern",
        text:
          "Sagen Sie survai, was Sie brauchen, und unsere KI generiert sofort Fragen und Antworten. Bearbeiten und ordnen Sie diese nahtlos neu.",
      },
      benefit3: {
        title: "Sprachbarrieren überwinden",
        text:
          "Mit nur einem Klick übersetzt survai Ihre Umfrage in bis zu acht Sprachen – ohne zusätzliche Einrichtung.",
      },
      benefit4: {
        title: "Feedback sammeln ohne Login",
        text:
          "Teilen Sie schnell einen einzigartigen Umfragelink, und Teilnehmer geben einfach ihren Namen ein, um teilzunehmen und Feedback zu hinterlassen.",
        exampleButton: "Live-Beispiel ansehen",
      },
    },
    valueProposition: {
      speed: { title: "Geschwindigkeit & Effizienz", text: "Von der Idee zur Umfrage in Minuten." },
      collaboration: { title: "Teilen & Zusammenarbeiten", text: "Laden Sie unbegrenzt Mitarbeiter ein." },
      global: { title: "Globale Reichweite", text: "Erreichen Sie internationale Zielgruppen mit sofortigen Übersetzungen." },
      noCoding: { title: "Kein Programmieren", text: "Ideal für Marketer, Forscher und HR-Teams." },
      scalable: { title: "Skalierbar & Sicher", text: "Enterprise-Sicherheit und flexible Pläne." },
    },
    faq: [
      {
        question: "Wie sicher sind meine Daten?",
        answer:
          "Ihre Daten werden mit Enterprise-Grade-Sicherheit, verschlüsseltem Speicher und regelmäßigen Audits geschützt.",
      },
      {
        question: "Kann ich meine Umfragen anpassen?",
        answer:
          "Ja, unser intuitiver Editor ermöglicht es Ihnen, jeden Aspekt Ihrer Umfrage anzupassen.",
      },
      {
        question: "Wie funktioniert die mehrsprachige Übersetzung?",
        answer:
          "Mit einem Klick übersetzt survai Ihre Umfrage in bis zu acht Sprachen mithilfe fortschrittlicher KI.",
      },
      {
        question: "Was mache ich, wenn ich Hilfe benötige?",
        answer:
          "Unser Support-Team steht Ihnen rund um die Uhr per Chat und E-Mail zur Verfügung.",
      },
      {
        question: "Welche Abonnementpläne gibt es?",
        answer:
          "Wir bieten flexible Pläne für Einzelpersonen, Teams, Agenturen und Unternehmen mit maßgeschneiderten Funktionen.",
      },
      {
        question: "Wie teile ich meine Umfrage?",
        answer:
          "Erzeugen Sie über unser Share-Modul einen einzigartigen Link – Teilnehmer können die Umfrage ohne Registrierung ansehen und kommentieren.",
      },
      {
        question: "Kann ich meine Umfragedaten exportieren?",
        answer:
          "Ja, Sie können Ihre Umfrage und Antworten in PDF-, CSV- und XLS-Formaten exportieren.",
      },
      {
        question: "Benötige ich Programmierkenntnisse?",
        answer:
          "Überhaupt nicht – survai ist für jeden konzipiert.",
      },
      {
        question: "Wie schnell kann ich mit survai eine Umfrage generieren?",
        answer:
          "Unser KI-System erstellt in wenigen Minuten eine voll funktionsfähige Umfrage.",
      },
    ],
    finalCTA: {
      headline: "Bereit, Ihre Umfragen zu transformieren?",
      text: "Erstellen, übersetzen und teilen Sie Ihre Umfragen mühelos mit survai.",
      button: "Jetzt kostenlos starten",
    },
    footer: { copy: "© 2025 survai. Alle Rechte vorbehalten." },
  },
  it: {
    title: "Piattaforma di Sviluppo Sondaggi AI | survai",
    metaDescription:
      "Crea, modifica e condividi sondaggi intelligenti e multilingue in pochi secondi con survai.",
    navbar: { subscribe: "Abbonati", login: "Accedi" },
    hero: {
      headline: "Genera sondaggi di alta qualità in pochi secondi",
      subheadline:
        "Crea, modifica e condividi sondaggi intelligenti, tradotti in otto lingue.",
      cta: "Inizia Gratis",
    },
    features: {
      benefit1: {
        title: "Sondaggi basati sulla conoscenza in pochi secondi",
        text:
          "Con esperienza reale del settore, il motore AI di survai è addestrato su anni di sviluppo professionale di sondaggi. Ogni domanda è realizzata con metodi di ricerca comprovati.",
      },
      benefit2: {
        title: "Affina, remix e amplia le tue domande con il minimo sforzo",
        text:
          "Comunica le tue esigenze a survai e la nostra AI genererà domande e risposte in un attimo. Modifica e riordina facilmente.",
      },
      benefit3: {
        title: "Supera le barriere linguistiche",
        text:
          "Con un solo clic, survai trasforma il tuo sondaggio in fino a otto lingue, senza necessità di ulteriori configurazioni.",
      },
      benefit4: {
        title: "Raccogli feedback senza costringere al login",
        text:
          "Condividi rapidamente un link unico per il sondaggio: i partecipanti inseriscono il loro nome per partecipare e commentare.",
        exampleButton: "Guarda un esempio dal vivo",
      },
    },
    valueProposition: {
      speed: { title: "Velocità ed Efficienza", text: "Dall'idea al sondaggio pronto in pochi minuti." },
      collaboration: { title: "Collaborazione", text: "Invita collaboratori illimitati facilmente." },
      global: { title: "Portata Globale", text: "Espandi il tuo pubblico internazionale con traduzioni istantanee." },
      noCoding: { title: "Nessun Codice Necessario", text: "Perfetto per marketer, ricercatori e team HR." },
      scalable: { title: "Scalabile e Sicuro", text: "Sicurezza di livello enterprise e piani flessibili." },
    },
    faq: [
      {
        question: "Quanto sono sicuri i miei dati?",
        answer:
          "I tuoi dati sono protetti con sicurezza di livello enterprise, memorizzazione crittografata e controlli regolari.",
      },
      {
        question: "Posso personalizzare i miei sondaggi?",
        answer:
          "Certamente! Il nostro editor intuitivo ti consente di modificare ogni aspetto del tuo sondaggio.",
      },
      {
        question: "Come funziona la traduzione multilanguage?",
        answer:
          "Con un clic, survai utilizza un'IA avanzata per tradurre il tuo sondaggio in fino a otto lingue.",
      },
      {
        question: "Cosa faccio se ho bisogno di assistenza?",
        answer:
          "Il nostro team di supporto è disponibile 24/7 tramite chat ed e-mail per aiutarti.",
      },
      {
        question: "Quali piani di abbonamento sono disponibili?",
        answer:
          "Offriamo piani flessibili per singoli, team, agenzie ed imprese, con funzionalità personalizzate.",
      },
      {
        question: "Come posso condividere il mio sondaggio?",
        answer:
          "Genera un link unico tramite il modulo di condivisione e distribuiscilo: i partecipanti possono visualizzare e commentare senza account.",
      },
      {
        question: "Posso esportare i dati del mio sondaggio?",
        answer:
          "Sì, puoi esportare i sondaggi e le risposte in PDF, CSV o XLS.",
      },
      {
        question: "Ho bisogno di competenze di programmazione?",
        answer:
          "Non proprio – survai è progettato per chiunque, senza necessità di conoscenze tecniche.",
      },
      {
        question: "Quanto velocemente posso generare un sondaggio con survai?",
        answer:
          "Il nostro sistema AI trasforma la tua idea in un sondaggio completo in pochi minuti.",
      },
    ],
    finalCTA: {
      headline: "Pronto a trasformare i tuoi sondaggi?",
      text: "Crea, traduci e condividi i tuoi sondaggi senza sforzo con survai.",
      button: "Inizia Gratis Ora",
    },
    footer: { copy: "© 2025 survai. Tutti i diritti riservati." },
  },
  pt: {
    title: "Plataforma de Desenvolvimento de Pesquisas AI | survai",
    metaDescription:
      "Crie, edite e compartilhe pesquisas inteligentes e multilíngues em segundos com o survai.",
    navbar: { subscribe: "Assinar", login: "Entrar" },
    hero: {
      headline: "Gere pesquisas de alta qualidade em segundos",
      subheadline:
        "Crie, edite e compartilhe pesquisas inteligentes – traduzidas para oito idiomas.",
      cta: "Comece Gratuitamente",
    },
    features: {
      benefit1: {
        title: "Pesquisas baseadas em conhecimento em segundos",
        text:
          "Com experiência real do setor, o motor de IA do survai é treinado com anos de desenvolvimento profissional. Confie que cada pergunta é elaborada com métodos comprovados.",
      },
      benefit2: {
        title: "Aperfeiçoe, remix e expanda suas perguntas com mínimo esforço",
        text:
          "Informe ao survai o que você precisa e nossa IA gera perguntas e respostas instantaneamente. Edite e reordene com facilidade.",
      },
      benefit3: {
        title: "Quebre barreiras linguísticas",
        text:
          "Com um clique, o survai traduz sua pesquisa para até oito idiomas, sem configurações adicionais.",
      },
      benefit4: {
        title: "Coleta feedback sem login",
        text:
          "Compartilhe rapidamente um link único, permitindo que os respondentes participem e comentem inserindo apenas o nome.",
        exampleButton: "Veja um exemplo ao vivo",
      },
    },
    valueProposition: {
      speed: { title: "Velocidade e Eficiência", text: "Da ideia à pesquisa pronta em minutos." },
      collaboration: { title: "Colaboração", text: "Convide colaboradores ilimitados com facilidade." },
      global: { title: "Alcance Global", text: "Expanda para públicos internacionais com traduções instantâneas." },
      noCoding: { title: "Sem Necessidade de Programação", text: "Perfeito para profissionais de marketing, pesquisadores e equipes de RH." },
      scalable: { title: "Escalável e Seguro", text: "Segurança de nível empresarial e planos flexíveis." },
    },
    faq: [
      {
        question: "Quão seguros estão meus dados?",
        answer:
          "Seus dados estão protegidos com segurança de nível empresarial, armazenamento criptografado e auditorias regulares.",
      },
      {
        question: "Posso personalizar minhas pesquisas?",
        answer:
          "Sim, nosso editor intuitivo permite ajustar todos os aspectos da sua pesquisa.",
      },
      {
        question: "Como funciona a tradução multilíngue?",
        answer:
          "Com um clique, o survai utiliza IA avançada para traduzir sua pesquisa em até oito idiomas.",
      },
      {
        question: "O que faço se precisar de ajuda?",
        answer:
          "Nossa equipe de suporte está disponível 24/7 via chat e e-mail para ajudar com qualquer dúvida.",
      },
      {
        question: "Quais planos de assinatura estão disponíveis?",
        answer:
          "Oferecemos planos flexíveis para indivíduos, equipes, agências e empresas, com recursos personalizados.",
      },
      {
        question: "Como compartilho minha pesquisa?",
        answer:
          "Gere um link único através do modal de compartilhamento e distribua-o – os participantes podem ver e comentar sem precisar de conta.",
      },
      {
        question: "Posso exportar os dados da minha pesquisa?",
        answer:
          "Sim, você pode exportar suas pesquisas e respostas em formatos PDF, CSV e XLS.",
      },
      {
        question: "Preciso ter habilidades de programação?",
        answer:
          "De forma alguma – o survai é projetado para todos, independentemente do conhecimento técnico.",
      },
      {
        question: "Quão rápido posso gerar uma pesquisa com o survai?",
        answer:
          "Nosso sistema baseado em IA transforma sua ideia em uma pesquisa completa em apenas alguns minutos.",
      },
    ],
    finalCTA: {
      headline: "Pronto para transformar suas pesquisas?",
      text: "Crie, traduza e compartilhe suas pesquisas sem esforço com o survai.",
      button: "Comece Gratuitamente Agora",
    },
    footer: { copy: "© 2025 survai. Todos os direitos reservados." },
  },
  zh: {
    title: "AI 调查开发平台 | survai",
    metaDescription:
      "使用 survai 在几秒钟内构建、编辑和分享智能多语言调查。",
    navbar: { subscribe: "订阅", login: "登录" },
    hero: {
      headline: "几秒钟内生成高质量调查",
      subheadline:
        "创建、编辑和分享智能调查——翻译成八种语言。",
      cta: "免费开始",
    },
    features: {
      benefit1: {
        title: "秒级生成基于知识的调查",
        text:
          "依托真实行业经验，survai 的 AI 引擎经过多年专业调查开发的训练。每个问题都采用经过验证的研究方法生成。",
      },
      benefit2: {
        title: "轻松微调、重组和扩展问题",
        text:
          "告诉 survai 您的需求，我们的 AI 将即时生成问题和答案，您可以随时编辑和重新排序。",
      },
      benefit3: {
        title: "跨越语言障碍",
        text:
          "只需一键，survai 就能将您的调查翻译成最多八种语言，无需额外设置。",
      },
      benefit4: {
        title: "无需登录即可收集反馈",
        text:
          "快速分享独特的调查链接，受访者只需输入姓名即可参与并留言。",
        exampleButton: "查看实时示例",
      },
    },
    valueProposition: {
      speed: { title: "速度与效率", text: "从创意到调查完成仅需几分钟。" },
      collaboration: { title: "协作", text: "轻松邀请无限合作者协同工作。" },
      global: { title: "全球覆盖", text: "借助即时翻译轻松拓展国际受众。" },
      noCoding: { title: "无需编程", text: "适用于市场营销人员、研究人员及人力资源团队。" },
      scalable: { title: "可扩展且安全", text: "企业级安全和灵活的计划。" },
    },
    faq: [
      {
        question: "我的数据有多安全？",
        answer:
          "您的数据通过企业级安全措施、加密存储和定期审计得到保护。",
      },
      {
        question: "我可以自定义调查吗？",
        answer:
          "当然可以！我们的直观编辑器让您可以调整调查的各个方面。",
      },
      {
        question: "多语言翻译如何工作？",
        answer:
          "只需一键，survai 利用先进的 AI 将您的调查翻译成最多八种语言。",
      },
      {
        question: "如果我需要帮助怎么办？",
        answer:
          "我们的支持团队通过在线聊天和电子邮件全天候为您提供帮助。",
      },
      {
        question: "有哪些订阅计划？",
        answer:
          "我们为个人、团队、代理机构及企业提供灵活的订阅计划，每个计划均提供量身定制的功能。",
      },
      {
        question: "我如何分享调查？",
        answer:
          "通过共享模块生成一个独特的链接，受访者无需注册即可查看和评论。",
      },
      {
        question: "我可以导出调查数据吗？",
        answer:
          "可以，您可以将调查及其响应数据导出为 PDF、CSV 或 XLS 格式。",
      },
      {
        question: "我需要编程技能吗？",
        answer:
          "完全不需要 —— survai 适用于所有用户，无需任何技术知识。",
      },
      {
        question: "我能多快生成调查？",
        answer:
          "使用我们的 AI 系统，您可以在几分钟内将创意转化为完整的调查。",
      },
    ],
    finalCTA: {
      headline: "准备好改变您的调查方式了吗？",
      text: "使用 survai 轻松创建、翻译和分享您的调查。",
      button: "立即免费开始",
    },
    footer: { copy: "© 2025 survai. 保留所有权利。" },
  },
  ja: {
    title: "AI 調査開発プラットフォーム | survai",
    metaDescription:
      "survai を使って、数秒でインテリジェントな多言語調査を作成、編集、共有できます。",
    navbar: { subscribe: "購読する", login: "ログイン" },
    hero: {
      headline: "数秒で高品質な調査を生成",
      subheadline:
        "8つの言語に翻訳されたインテリジェントな調査を、すぐに作成、編集、共有。",
      cta: "無料で始める",
    },
    features: {
      benefit1: {
        title: "知識に基づく調査を瞬時に生成",
        text:
          "実績ある業界経験に裏打ちされた survai の AI エンジンは、何年にもわたるプロフェッショナルな調査開発で鍛えられています。すべての質問は実証済みの手法で作成されます。",
      },
      benefit2: {
        title: "質問を簡単に微調整・リミックス・拡張",
        text:
          "必要な情報を伝えるだけで、survai の AI が瞬時に質問と回答を生成。シームレスに編集や並べ替えが可能です。",
      },
      benefit3: {
        title: "言語の壁を超える",
        text:
          "ワンクリックで survai は調査を最大8言語に翻訳。追加の設定は不要です。",
      },
      benefit4: {
        title: "ログイン不要でフィードバックを収集",
        text:
          "ユニークな調査リンクをすばやく共有し、名前を入力するだけで参加・コメント可能。",
        exampleButton: "ライブ例を見る",
      },
    },
    valueProposition: {
      speed: { title: "スピードと効率", text: "アイデアからすぐに送信可能な調査へ、数分で変換。" },
      collaboration: { title: "協力と共有", text: "無制限の協力者を簡単に招待できます。" },
      global: { title: "グローバルリーチ", text: "即時翻訳で世界中のオーディエンスにアプローチ。" },
      noCoding: { title: "コーディング不要", text: "マーケター、研究者、人事担当者に最適です。" },
      scalable: { title: "スケーラブルで安全", text: "エンタープライズレベルのセキュリティと柔軟なプラン。" },
    },
    faq: [
      {
        question: "私のデータはどの程度安全ですか？",
        answer:
          "あなたのデータは、エンタープライズレベルのセキュリティ、暗号化保存、定期的な監査で保護されています。",
      },
      {
        question: "調査はカスタマイズできますか？",
        answer:
          "はい、直感的なエディターで調査のあらゆる側面を調整できます。",
      },
      {
        question: "多言語翻訳はどう機能しますか？",
        answer:
          "ワンクリックで survai は高度な AI を利用し、調査を最大8言語に翻訳します。",
      },
      {
        question: "ヘルプが必要な場合はどうすればよいですか？",
        answer:
          "サポートチームがチャットとメールで24時間対応します。",
      },
      {
        question: "利用可能なサブスクリプションプランは何ですか？",
        answer:
          "個人、チーム、エージェンシー、企業向けの柔軟なプランをご用意しています。",
      },
      {
        question: "調査はどのように共有できますか？",
        answer:
          "共有モーダルからユニークなリンクを生成し、配布するだけで、アカウント不要で閲覧・コメントできます。",
      },
      {
        question: "調査データはエクスポートできますか？",
        answer:
          "はい、PDF、CSV、XLS形式で調査と回答データをエクスポートできます。",
      },
      {
        question: "プログラミングスキルは必要ですか？",
        answer:
          "いいえ、survai はすべてのユーザー向けに設計されており、技術的な知識は不要です。",
      },
      {
        question: "survaiでどれくらい速く調査を生成できますか？",
        answer:
          "数分でアイデアを完全な調査に変換できます。",
      },
    ],
    finalCTA: {
      headline: "あなたの調査を変革する準備はできていますか？",
      text: "survaiを使って、調査の作成、翻訳、共有を簡単に実現しましょう。",
      button: "今すぐ無料で始める",
    },
    footer: { copy: "© 2025 survai. 全著作権所有。" },
  },
  ko: {
    title: "AI 설문조사 개발 플랫폼 | survai",
    metaDescription:
      "survai를 사용하여 몇 초 만에 지능형 다국어 설문조사를 생성, 편집 및 공유하세요.",
    navbar: { subscribe: "구독하기", login: "로그인" },
    hero: {
      headline: "몇 초 만에 고품질 설문조사를 생성하세요",
      subheadline:
        "8개 언어로 번역된 지능형 설문조사를 즉시 생성, 편집 및 공유하세요.",
      cta: "무료로 시작하기",
    },
    features: {
      benefit1: {
        title: "지식 기반 설문조사를 순식간에 생성",
        text:
          "실제 산업 경험을 바탕으로 survai의 AI 엔진은 수년간의 전문 설문조사 개발로 훈련되었습니다. 모든 질문은 검증된 연구 방법으로 작성됩니다.",
      },
      benefit2: {
        title: "질문을 손쉽게 미세 조정, 리믹스 및 확장",
        text:
          "필요한 내용을 survai에 알려주시면 AI가 즉시 질문과 답변을 생성합니다. 자유롭게 편집하고 순서를 재배열하세요.",
      },
      benefit3: {
        title: "언어 장벽 극복",
        text:
          "한 번의 클릭으로 survai는 설문조사를 최대 8개 언어로 번역합니다. 추가 설정은 필요 없습니다.",
      },
      benefit4: {
        title: "로그인 없이 피드백 수집",
        text:
          "고유한 설문조사 링크를 빠르게 공유하여 응답자가 이름만 입력하면 참여하고 댓글을 남길 수 있습니다.",
        exampleButton: "실시간 예시 보기",
      },
    },
    valueProposition: {
      speed: { title: "속도와 효율성", text: "아이디어에서 전송 가능한 설문조사까지 몇 분 만에 완성됩니다." },
      collaboration: { title: "협업", text: "무제한 협업자를 손쉽게 초대할 수 있습니다." },
      global: { title: "글로벌 도달", text: "즉시 번역을 통해 전 세계 청중에게 도달하세요." },
      noCoding: { title: "코딩 불필요", text: "마케터, 연구원, 인사팀 등 모두를 위한 설문조사 도구입니다." },
      scalable: { title: "확장 가능하고 안전함", text: "엔터프라이즈 수준의 보안과 유연한 플랜을 제공합니다." },
    },
    faq: [
      {
        question: "내 데이터는 얼마나 안전한가요?",
        answer:
          "귀하의 데이터는 엔터프라이즈 수준의 보안, 암호화된 저장 및 정기적인 감사를 통해 보호됩니다.",
      },
      {
        question: "설문조사를 맞춤 설정할 수 있나요?",
        answer:
          "네, 직관적인 편집기를 통해 설문조사의 모든 측면을 조정할 수 있습니다.",
      },
      {
        question: "다국어 번역은 어떻게 작동하나요?",
        answer:
          "한 번의 클릭으로 survai는 고급 AI를 사용하여 설문조사를 최대 8개 언어로 번역합니다.",
      },
      {
        question: "도움이 필요하면 어떻게 하나요?",
        answer:
          "우리의 지원팀은 채팅과 이메일을 통해 24시간 도움을 드립니다.",
      },
      {
        question: "어떤 구독 플랜이 제공되나요?",
        answer:
          "개인, 팀, 에이전시, 기업용 유연한 구독 플랜을 제공하며, 각각의 기능이 맞춤화되어 있습니다.",
      },
      {
        question: "설문조사는 어떻게 공유하나요?",
        answer:
          "공유 모달을 통해 고유한 링크를 생성하여, 응답자가 계정 없이 설문조사를 볼 수 있습니다.",
      },
      {
        question: "설문조사 데이터를 내보낼 수 있나요?",
        answer:
          "예, PDF, CSV, XLS 등 다양한 형식으로 데이터를 내보낼 수 있습니다.",
      },
      {
        question: "코딩 기술이 필요한가요?",
        answer:
          "전혀 필요하지 않습니다 – survai는 모든 사용자를 위해 설계되었습니다.",
      },
      {
        question: "survai로 설문조사를 얼마나 빨리 생성할 수 있나요?",
        answer:
          "우리의 AI 시스템을 사용하면 몇 분 만에 아이디어를 완전한 설문조사로 전환할 수 있습니다.",
      },
    ],
    finalCTA: {
      headline: "설문조사를 혁신할 준비가 되셨나요?",
      text: "survai를 사용하여 설문조사를 쉽게 생성, 번역 및 공유하세요.",
      button: "지금 무료로 시작하기",
    },
    footer: { copy: "© 2025 survai. 모든 권리 보유." },
  },
};

const languageOptions = [
  { code: "en", label: "EN" },
  { code: "es", label: "ES" },
  { code: "fr", label: "FR" },
  { code: "de", label: "DE" },
  { code: "it", label: "IT" },
  { code: "pt", label: "PR" },
  { code: "zh", label: "CN" },
  { code: "ja", label: "JP" },
  { code: "ko", label: "KR" },
];

const LandingPage = () => {
  const [currentLanguage, setCurrentLanguage] = useState("en");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [openFAQIndex, setOpenFAQIndex] = useState(null);
  const languageSelectorRef = useRef(null);

  // Get translations for the current language; fallback to English if missing.
  const t = translations[currentLanguage] || translations.en;

  // Toggle logic for FAQ items
  const handleToggleFAQ = (index) => {
    setOpenFAQIndex(openFAQIndex === index ? null : index);
  };

  // Auto-close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        languageSelectorRef.current &&
        !languageSelectorRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="landing-page">
      <Helmet>
        <title>{t.title}</title>
        <meta name="description" content={t.metaDescription} />
      </Helmet>

      {/* Navbar */}
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-logo">
            <img src="/survailogo.svg" alt="survai Logo" />
          </div>
          <div className="navbar-buttons">
            <a href="/subscribe" className="navbar-button subscribe">
              {t.navbar.subscribe}
            </a>
            <a href="/login" className="navbar-button login">
              {t.navbar.login}
            </a>
            {/* Custom Language Selector */}
            <div className="language-selector" ref={languageSelectorRef}>
  <button
    className="language-selector-button"
    onClick={() => setDropdownOpen(!dropdownOpen)}
  >
    <i className="fa-light fa-language"></i>
    <span>{currentLanguage.toUpperCase()}</span>
  </button>
  {dropdownOpen && (
    <ul className="language-dropdown">
      {languageOptions.map((lang) => (
        <li
          key={lang.code}
          className="language-option"
          onClick={() => {
            setCurrentLanguage(lang.code);
            setDropdownOpen(false);
          }}
        >
          <span>{lang.label}</span>
        </li>
      ))}
    </ul>
  )}
</div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <h1>{t.hero.headline}</h1>
          <p>{t.hero.subheadline}</p>
          <button className="cta-button">{t.hero.cta}</button>
        </div>
        <div className="hero-visual">
          <img src="/Dashboard.svg" alt="survai interface demo" />
        </div>
      </section>

      {/* Features Section */}
      <section className="features wise-style">
        <div className="benefit-row">
          <div className="benefit-text">
            <h3>{t.features.benefit1.title}</h3>
            <p>{t.features.benefit1.text}</p>
          </div>
          <div className="benefit-image">
            <img src="/Types.svg" alt="Easy editing & Question generation" />
          </div>
        </div>
        <div className="benefit-row reversed">
          <div className="benefit-text">
            <h3>{t.features.benefit2.title}</h3>
            <p>{t.features.benefit2.text}</p>
          </div>
          <div className="benefit-image">
            <img src="/Questions.svg" alt="Easy editing & Question generation" />
          </div>
        </div>
        <div className="benefit-row">
          <div className="benefit-text">
            <h3>{t.features.benefit3.title}</h3>
            <p>{t.features.benefit3.text}</p>
          </div>
          <div className="benefit-image">
            <img src="/Translate.svg" alt="Easy Translation" />
          </div>
        </div>
        <div className="benefit-row reversed">
          <div className="benefit-text">
            <h3>{t.features.benefit4.title}</h3>
            <p>{t.features.benefit4.text}</p>
            <a
              href="https://survai.co/shared/39a1bc87583a098e6181a9b74dbe8e12"
              className="example-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fa-regular fa-square-poll-horizontal"></i>
              <span>{t.features.benefit4.exampleButton}</span>
            </a>
          </div>
          <div className="benefit-image">
            <img src="/Shared.svg" alt="Share" />
          </div>
        </div>
      </section>
      

      {/* Value Proposition Section */}
      <section className="value-proposition">
        <div className="value-proposition-grid">
          <div className="value-proposition-item">
            <i className="fa-regular fa-rabbit-running"></i>
            <h3>{t.valueProposition.speed.title}</h3>
            <p>{t.valueProposition.speed.text}</p>
          </div>
          <div className="value-proposition-item">
            <i className="fa-regular fa-handshake-simple"></i>
            <h3>{t.valueProposition.collaboration.title}</h3>
            <p>{t.valueProposition.collaboration.text}</p>
          </div>
          <div className="value-proposition-item">
            <i className="fa-regular fa-earth-americas"></i>
            <h3>{t.valueProposition.global.title}</h3>
            <p>{t.valueProposition.global.text}</p>
          </div>
          <div className="value-proposition-item">
            <i className="fa-regular fa-code"></i>
            <h3>{t.valueProposition.noCoding.title}</h3>
            <p>{t.valueProposition.noCoding.text}</p>
          </div>
          <div className="value-proposition-item">
            <i className="fa-regular fa-shield-check"></i>
            <h3>{t.valueProposition.scalable.title}</h3>
            <p>{t.valueProposition.scalable.text}</p>
          </div>
        </div>
      </section>
      <section className="pricing">
      <h2>Pricing plans & questions</h2>
  <div className="pricing-cards">
    <div className="pricing-card">
      <h3>Pay as you go</h3>
      <h4>$2</h4>
      <p className="pm">per survey</p>
      <p className="ideal">For research and marketing individuals.</p>
      <ul>
        <li>1x user</li>
        <li>Purchase survey tokens</li>
        <li>survai branding</li>
      </ul>
      <button className="cta-button">Subscribe</button>
    </div>
    <div className="pricing-card">
      <h3>Individual</h3>
      <h4>$18</h4>
      <p className="pm">per month</p>
      <p className="ideal">For teams creating surveys for individual projects.</p>
      <ul>
        <li>1x user</li>
        <li>Unlimited surveys</li>
        <li>survai branding</li>
        <li>Manage users on dashboard</li>
      </ul>
      <button className="cta-button">Subscribe</button>
    </div>
    <div className="pricing-card recommended">
      <h3>Agency / Team</h3>
      <h4>$80</h4>
      <p className="pm">per month</p>
      <p className="ideal">Ideal for agencies with multiple clients.</p>
      <ul>
        <li>Up to 15 users</li>
        <li>Unlimited surveys</li>
        <li>Custom branding</li>
        <li>Shared survey workspace</li>
        <li>Dedicated agency dashboard</li>
      </ul>
      <button className="cta-button">Subscribe</button>
    </div>
    <div className="pricing-card">
      <h3>Enterprise</h3>
      <h4>$200</h4>
      <p className="pm">per month</p>
      <p className="ideal">Tailored for large organizations with custom needs.</p>
      <ul>
        <li>Unlimited users</li>
        <li>Unlimited surveys</li>
        <li>Custom branding</li>
        <li>Host on your own domain</li>
        <li>Choose AI model</li>
        <li>Dedicated support</li>
      </ul>
      <button className="cta-button">Subscribe</button>
    </div>
  </div>
</section>
      {/* FAQ Section */}
      <section className="faq">
        {t.faq.map((item, idx) => (
          <div className="faq-item" key={idx}>
            <div className="faq-question" onClick={() => handleToggleFAQ(idx)}>
              <h3>{item.question}</h3>
              <span className="faq-toggle-icon">
                {openFAQIndex === idx ? "-" : "+"}
              </span>
            </div>
            {openFAQIndex === idx && <p className="faq-answer">{item.answer}</p>}
          </div>
        ))}
      </section>

      {/* Final CTA Section */}
      <section className="final-cta">
        <h2>{t.finalCTA.headline}</h2>
        <p>{t.finalCTA.text}</p>
      </section>

      {/* Footer Section */}
      <footer className="footer">
        <p className="footer-copy">{t.footer.copy}</p>
      </footer>
    </div>
  );
};

export default LandingPage;
